import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createFirstAdmin, checkSetupStatus } from '../../services/userService';
import '../../styles/admin.css';

export default function FirstRunSetupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '', fullName: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkSetupStatus()
      .then(data => {
        if (data?.adminExists) {
          navigate('/login', { replace: true });
        }
      })
      .catch(() => {
        // If Cloud Functions not yet deployed, allow setup to proceed
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  }

  function validate() {
    if (!form.fullName.trim()) return 'Full name is required';
    if (!form.email.trim()) return 'Email address is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Enter a valid email address';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    setError('');
    try {
      await createFirstAdmin({
        email: form.email,
        password: form.password,
        fullName: form.fullName,
      });
      navigate('/login');
    } catch (err) {
      if (err.message?.includes('already exists') || err.message?.includes('admin')) {
        setError('An admin account already exists. Please log in.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(err.message || 'Setup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (checking) {
    return (
      <div className="auth-page">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 460 }}>
        <div className="auth-logo">
          <h1>Mirepa Investment Advisors</h1>
          <p>Deal Origination & Portfolio Management Portal</p>
        </div>

        <h2 className="auth-title">First-Time Setup</h2>
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 24 }}>
          Create the first administrator account for this portal. This setup screen is only available once.
        </p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-field" style={{ marginBottom: 16 }}>
            <label className="form-label">Full Name <span className="required">*</span></label>
            <input className="form-input" value={form.fullName}
              onChange={e => set('fullName', e.target.value)} placeholder="e.g. John Doe" />
          </div>
          <div className="form-field" style={{ marginBottom: 16 }}>
            <label className="form-label">Email Address <span className="required">*</span></label>
            <input type="email" className="form-input" value={form.email}
              onChange={e => set('email', e.target.value)} placeholder="admin@mirepa.com" />
          </div>
          <div className="form-field" style={{ marginBottom: 16 }}>
            <label className="form-label">Password <span className="required">*</span></label>
            <input type="password" className="form-input" value={form.password}
              onChange={e => set('password', e.target.value)} placeholder="Min 8 characters" />
          </div>
          <div className="form-field" style={{ marginBottom: 24 }}>
            <label className="form-label">Confirm Password <span className="required">*</span></label>
            <input type="password" className="form-input" value={form.confirmPassword}
              onChange={e => set('confirmPassword', e.target.value)} placeholder="Re-enter password" />
          </div>

          <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
            {loading ? <span className="spinner spinner-sm" /> : null}
            {loading ? 'Creating account…' : 'Create Admin Account'}
          </button>
        </form>
      </div>
    </div>
  );
}
